import * as React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import type { XYCoord, Identifier } from 'dnd-core';
import { styled } from '@mui/material';
import { CerebroIcon, CerebroText } from '.';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';

const ItemTypes = {
	CARD: 'card',
};

export interface CardProps {
	id: any;
	text: string;
	index: number;
	onChange: (dragIndex: number, hoverIndex: number) => void;
}

interface DragItem {
	index: number;
	id: string;
	type: string;
}

export const DealPreferenceDraggableCard: React.FC<CardProps> = ({ id, text, index, onChange }) => {
	const ref = React.useRef<HTMLDivElement>(null);
	const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
		accept: ItemTypes.CARD,
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId(),
			};
		},
		hover(item: DragItem, monitor) {
			if (!ref.current) {
				return;
			}
			const dragIndex = item.index;
			const hoverIndex = index;

			// Don't replace items with themselves
			if (dragIndex === hoverIndex) {
				return;
			}

			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect();

			// Get vertical middle
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

			// Determine mouse position
			const clientOffset = monitor.getClientOffset();

			// Get pixels to the top
			const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}

			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}

			// Time to actually perform the action
			onChange(dragIndex, hoverIndex);
			item.index = hoverIndex;
		},
	});

	const [{ isDragging }, drag] = useDrag({
		type: ItemTypes.CARD,
		item: () => {
			return { id, index };
		},
		collect: (monitor: any) => ({
			isDragging: monitor.isDragging(),
		}),
	});

	const opacity = isDragging ? 0.1 : 1;
	drag(drop(ref));

	return (
		<StyledDraggableCardWrapper isDragging={isDragging} ref={ref} data-handler-id={handlerId}>
			<CerebroIcon padRight>
				<FontAwesomeIcon icon={faGripDotsVertical} />
			</CerebroIcon>
			<CerebroText>{text}</CerebroText>
		</StyledDraggableCardWrapper>
	);
};

type DraggableCardProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
	isDragging: boolean;
};

// eslint-disable-next-line react/display-name
const DraggableCard = React.forwardRef<HTMLDivElement, DraggableCardProps>((props, ref) => (
	<div ref={ref} {...props} />
));

const StyledDraggableCardWrapper = styled(DraggableCard, { shouldForwardProp: prop => prop !== 'isDragging' })(
	({ isDragging, theme }) => ({
		backgroundColor: theme.palette.common.white,
		borderWidth: 1,
		borderStyle: 'solid',
		borderColor: isDragging ? theme.palette.primary.main : theme.palette.gray.medium,
		borderRadius: theme.shape.borderRadius,
		boxShadow: isDragging ? theme.yellowShadows[3] : theme.shadows[3],
		borderLeftWidth: 4,

		cursor: 'grab',

		alignItems: 'center',
		display: 'flex',

		padding: theme.spacing(1.5),
		margin: theme.spacing(2, 0),
	})
);
